import { useEffect } from 'react';

/**
 * Whether the person has started something a reload would throw away.
 *
 * The update flow (`./updates`) reloads the page on its own in two cases --
 * a new version found as the app launches, and another window having already
 * switched to one -- and it may only do that while nothing is at stake. This
 * module is how the rest of the app says "not now":
 *
 * - {@link useHoldUpdates} from a component: the composer while it is open,
 *   every settings card while it has unsaved edits (via the unsaved-changes
 *   guard), anything else that owns work in progress.
 * - {@link holdUpdates} from plain code: the Outbox flush while it sends.
 * - {@link registerBusyProbe} for state that already lives in a store and is
 *   cheaper to ask than to mirror (a send waiting out its Undo window).
 *
 * A hold never stops a reload the person asked for (Reload in the prompt or in
 * Settings); it only turns an automatic one into the prompt.
 */

const holds = new Map<number, string>();
const probes = new Set<() => boolean>();
const holdListeners = new Set<() => void>();
let nextId = 1;

function holdsChanged(): void {
  for (const listener of holdListeners) listener();
}

/** Hold automatic reloads until the returned release is called. Idempotent release. */
export function holdUpdates(reason: string): () => void {
  const id = nextId++;
  holds.set(id, reason);
  holdsChanged();
  return () => {
    if (holds.delete(id)) holdsChanged();
  };
}

/**
 * `useSyncExternalStore` plumbing for {@link isHeld}: the update prompt keeps
 * the ordinary "a new version is ready" strip out of the way while something
 * is held (a composer open, unsaved settings), and shows it on release.
 */
export function subscribeHolds(listener: () => void): () => void {
  holdListeners.add(listener);
  return () => {
    holdListeners.delete(listener);
  };
}

/**
 * True while any hold is active. Probes are not included: they are asked, not
 * observed, and the one registered today (a send in its Undo window) already
 * defers the prompt through its snackbar.
 */
export function isHeld(): boolean {
  return holds.size > 0;
}

/** Holds automatic reloads while `active` is true and the caller is mounted. */
export function useHoldUpdates(active: boolean, reason: string): void {
  useEffect(() => {
    if (!active) return;
    return holdUpdates(reason);
  }, [active, reason]);
}

/** Adds a check that answers true while a reload would lose something. */
export function registerBusyProbe(probe: () => boolean): () => void {
  probes.add(probe);
  return () => {
    probes.delete(probe);
  };
}

/** Why the app is busy, for tests and debugging; empty when idle. */
export function busyReasons(): string[] {
  const reasons = [...holds.values()];
  for (const probe of probes) {
    try {
      if (probe()) reasons.push('probe');
    } catch {
      // A probe that throws cannot vouch for anything: treat it as busy.
      reasons.push('probe');
    }
  }
  return reasons;
}

/** True when nothing is held: an automatic reload would lose nothing. */
export function isAppIdle(): boolean {
  return busyReasons().length === 0;
}

/** Test seam. */
export function resetBusyForTests(): void {
  holds.clear();
  probes.clear();
  holdListeners.clear();
}
